const STAR_PATH =
  'M12 1.5l2.47 6.12 6.64.55-5.08 4.4 1.52 6.43L12 15.9l-5.55 3.1 1.52-6.43-5.08-4.4 6.64-.55L12 1.5z';

/**
 * @typedef {object} TestimonialReview
 * @property {string} id
 * @property {string} title
 * @property {string} body
 * @property {number} rating
 * @property {string} name
 * @property {string} [image]
 */

class TestimonialsCarousel extends HTMLElement {
  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#track = this.querySelector('[data-testimonials-track]');
    this.#layout = this.querySelector('[data-testimonials-layout]');
    this.#avatars = this.querySelector('[data-testimonials-avatars]');
    this.#rating = this.querySelector('[data-testimonials-rating]');
    this.#prev = this.querySelector('[data-testimonials-prev]');
    this.#next = this.querySelector('[data-testimonials-next]');
    this.#status = this.querySelector('[data-testimonials-status]');
    this.#progress = this.querySelector('[data-testimonials-progress]');
    this.#progressFill = this.querySelector('[data-testimonials-progress-fill]');
    this.#reviewTemplate = this.querySelector('[data-review-template]');
    this.#sourceTemplate = this.querySelector('[data-judgeme-source]');
    this.#desktopQuery = window.matchMedia('(min-width: 767px)');

    this.#prev?.addEventListener('click', (event) => {
      event.preventDefault();
      this.#scrollBy(-1);
    });
    this.#next?.addEventListener('click', (event) => {
      event.preventDefault();
      this.#scrollBy(1);
    });
    this.#track?.addEventListener('scroll', () => this.#syncNav(), { passive: true });
    this.#desktopQuery.addEventListener('change', this.#onDesktopChange);
    window.addEventListener('resize', () => this.#syncNav(), { passive: true });

    this.#renderSummary();
    this.#loadReviews();
  }

  disconnectedCallback() {
    this.#desktopQuery?.removeEventListener('change', this.#onDesktopChange);
  }

  #onDesktopChange = () => {
    if (this.#loadedReviews.length) this.#applyReviews(this.#loadedReviews);
    this.#syncNav();
  };

  /** @type {boolean} */
  #initialized = false;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #layout = null;

  /** @type {HTMLElement | null} */
  #avatars = null;

  /** @type {HTMLElement | null} */
  #rating = null;

  /** @type {HTMLButtonElement | null} */
  #prev = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {HTMLElement | null} */
  #status = null;

  /** @type {HTMLElement | null} */
  #progress = null;

  /** @type {HTMLElement | null} */
  #progressFill = null;

  /** @type {HTMLTemplateElement | null} */
  #reviewTemplate = null;

  /** @type {HTMLTemplateElement | null} */
  #sourceTemplate = null;

  /** @type {MediaQueryList | null} */
  #desktopQuery = null;

  /** @type {TestimonialReview[]} */
  #loadedReviews = [];

  get #maxReviews() {
    return 20;
  }

  get #anonymousLabel() {
    return this.dataset.anonymousLabel || 'Anonymous';
  }

  async #loadReviews() {
    this.setAttribute('aria-busy', 'true');

    try {
      const reviews = await this.#resolveReviews();
      this.#applyReviews(reviews);
    } catch {
      this.#setStatus(this.dataset.emptyLabel || '');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /** @returns {Promise<TestimonialReview[]>} */
  async #resolveReviews() {
    const featured = this.#uniqueReviews([
      ...this.#reviewsFromSourceTemplate(),
      ...(await this.#reviewsFromPublicWidgets('featured')),
    ]);
    if (featured.length >= this.#maxReviews) return featured;

    const mixed = this.#uniqueReviews([
      ...featured,
      ...this.#reviewsFromStoreTemplate(),
      ...(await this.#reviewsFromPublicWidgets('fill')),
    ]);
    if (mixed.length) return mixed;

    return this.#placeholderReviews();
  }

  /**
   * @param {TestimonialReview[]} reviews
   */
  #applyReviews(reviews) {
    this.#loadedReviews = reviews;
    const limited = reviews.slice(0, this.#maxReviews);

    if (!limited.length) {
      this.#setStatus(this.dataset.emptyLabel || '');
      return;
    }

    this.#renderReviews(limited);
    this.#renderAvatars(limited);
    this.#renderSummary();
    this.#setStatus('');
    this.removeAttribute('aria-busy');
    requestAnimationFrame(() => {
      this.#syncNav();
      requestAnimationFrame(() => this.#syncNav());
    });
  }

  /** @returns {TestimonialReview[]} */
  #reviewsFromStoreTemplate() {
    const node = this.querySelector('[data-store-reviews-source]');
    if (!(node instanceof HTMLTemplateElement)) return [];

    const wrapper = document.createElement('div');
    wrapper.append(node.content.cloneNode(true));
    return this.#reviewsFromHtml(wrapper.innerHTML);
  }

  /** @returns {TestimonialReview[]} */
  #reviewsFromSourceTemplate() {
    if (!this.#sourceTemplate) return [];

    const wrapper = document.createElement('div');
    wrapper.append(this.#sourceTemplate.content.cloneNode(true));
    return this.#reviewsFromHtml(wrapper.innerHTML);
  }

  /**
   * @param {TestimonialReview[]} reviews
   * @returns {TestimonialReview[]}
   */
  #uniqueReviews(reviews) {
    const seen = new Set();
    const unique = [];

    for (const review of reviews) {
      if (!review) continue;
      const key = String(review.id || `${review.name}-${review.body}`.slice(0, 80));
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(review);
    }

    return unique;
  }

  /**
   * @param {string} html
   * @returns {TestimonialReview[]}
   */
  #reviewsFromHtml(html) {
    const decoded = this.#decodeSource(html);
    if (!decoded) return [];

    if (decoded.startsWith('{') || decoded.startsWith('[')) {
      try {
        return this.#normalizePayload(JSON.parse(decoded));
      } catch {
        // Fall through to HTML parsing.
      }
    }

    return this.#parseHtml(decoded);
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #decodeSource(value) {
    let html = (value || '').trim();
    if (!html) return '';

    if (!html.includes('<') && html.includes('&lt;')) {
      const decoder = document.createElement('textarea');
      decoder.innerHTML = html;
      html = decoder.value.trim();
    }

    return html;
  }

  /**
   * Load published reviews from Judge.me's public Widget API only.
   * Never call `/api/v1/reviews` from the theme — that Reviews API needs the
   * private token and must not be used in Liquid, JS, or section settings.
   *
   * @param {'featured' | 'fill'} source
   * @returns {Promise<TestimonialReview[]>}
   */
  async #reviewsFromPublicWidgets(source = 'featured') {
    const shopDomain = this.dataset.shopDomain || '';
    const publicToken = this.dataset.publicToken || '';
    if (!shopDomain || !publicToken) return [];

    const hosts = ['https://judge.me/api/v1', 'https://api.judge.me/api/v1'];
    const featuredQueries = [
      `widgets/featured_carousel?shop_domain=${encodeURIComponent(shopDomain)}&api_token=${encodeURIComponent(publicToken)}&json_request=true`,
      `widgets/featured_carousel?shop_domain=${encodeURIComponent(shopDomain)}&api_token=${encodeURIComponent(publicToken)}`,
    ];
    const fillQueries = [
      `widgets/all_reviews_page?shop_domain=${encodeURIComponent(shopDomain)}&api_token=${encodeURIComponent(publicToken)}&review_type=shop-reviews&json_request=true&per_page=25`,
      `widgets/all_reviews_page?shop_domain=${encodeURIComponent(shopDomain)}&api_token=${encodeURIComponent(publicToken)}&review_type=product-reviews&json_request=true&per_page=25`,
    ];
    const queries = source === 'fill' ? fillQueries : featuredQueries;

    const collected = [];

    for (const host of hosts) {
      let found = false;

      for (const query of queries) {
        const pages = source === 'fill' ? 2 : 1;

        for (let page = 1; page <= pages; page += 1) {
          const url = `${host}/${query}&page=${page}`;
          if (!/^https:\/\/(api\.)?judge\.me\/api\/v1\/widgets\//.test(url)) continue;

          try {
            const response = await fetch(url, { credentials: 'omit', mode: 'cors' });
            if (!response.ok) continue;

            const payload = this.#parseResponse(await response.text(), response.headers.get('content-type') || '');
            this.#applySummaryFromPayload(payload);
            const reviews = this.#normalizePayload(payload);
            if (!reviews.length) continue;

            collected.push(...reviews);
            found = true;
            if (this.#uniqueReviews(collected).length >= this.#maxReviews) break;
          } catch {
            // Try the next Judge.me widget endpoint.
          }
        }

        if (this.#uniqueReviews(collected).length >= this.#maxReviews) break;
      }

      if (found) break;
    }

    return this.#uniqueReviews(collected);
  }

  /**
   * @param {string} text
   * @param {string} contentType
   * @returns {unknown}
   */
  #parseResponse(text, contentType) {
    const trimmed = (text || '').trim();
    if (!trimmed) return '';

    const jsonp = trimmed.match(/^[a-zA-Z_$][\w$]*\(([\s\S]*)\)\s*;?$/);
    const candidate = jsonp ? jsonp[1] : trimmed;

    if (contentType.includes('application/json') || candidate.startsWith('{') || candidate.startsWith('[')) {
      try {
        return JSON.parse(candidate);
      } catch {
        return trimmed;
      }
    }

    return trimmed;
  }

  /** @returns {TestimonialReview[]} */
  #placeholderReviews() {
    const node = this.querySelector('[data-placeholder-reviews]');
    if (!(node instanceof HTMLScriptElement) || !node.textContent) return [];

    try {
      const parsed = JSON.parse(node.textContent);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((item, index) => this.#fromObject(item, index)).filter(Boolean);
    } catch {
      return [];
    }
  }

  /**
   * @param {unknown} payload
   * @returns {TestimonialReview[]}
   */
  #normalizePayload(payload) {
    if (!payload) return [];

    if (typeof payload === 'string') {
      return this.#parseHtml(payload);
    }

    if (Array.isArray(payload)) {
      const fromItems = payload.flatMap((item, index) => {
        const review = this.#fromObject(item, index);
        if (review) return [review];
        return this.#normalizePayload(item);
      });
      return this.#uniqueReviews(fromItems);
    }

    if (typeof payload !== 'object') return [];

    const data = /** @type {Record<string, unknown>} */ (payload);
    const collected = [];

    if (Array.isArray(data.reviews)) {
      collected.push(...data.reviews.map((item, index) => this.#fromObject(item, index)).filter(Boolean));
    } else if (data.reviews && typeof data.reviews === 'object') {
      collected.push(...this.#normalizePayload(data.reviews));
    }

    for (const key of ['shop_reviews', 'store_reviews', 'product_reviews', 'shopReviews', 'productReviews']) {
      if (Array.isArray(data[key])) {
        collected.push(...data[key].map((item, index) => this.#fromObject(item, index)).filter(Boolean));
      }
    }

    if (data.reviewWidget && typeof data.reviewWidget === 'object') {
      collected.push(...this.#normalizePayload(data.reviewWidget));
    }

    if (data.allReviewsWidgetV2025 && typeof data.allReviewsWidgetV2025 === 'object') {
      collected.push(...this.#normalizePayload(data.allReviewsWidgetV2025));
    }

    if (data.data && typeof data.data === 'object' && collected.length === 0) {
      collected.push(...this.#normalizePayload(data.data));
    }

    const htmlCandidates = [
      data.html,
      data.featured_carousel,
      data.all_reviews,
      data.all_reviews_page,
      data.widget,
    ];
    for (const html of htmlCandidates) {
      if (typeof html === 'string' && html.trim()) {
        collected.push(...this.#parseHtml(html));
      }
    }

    return this.#uniqueReviews(collected.filter(Boolean));
  }

  /**
   * @param {unknown} payload
   */
  #applySummaryFromPayload(payload) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return;

    const data = /** @type {Record<string, unknown>} */ (payload);
    const rating = Number(data.average_rating ?? data.all_reviews_rating ?? data.rating);
    const count = Number(data.number_of_reviews ?? data.all_reviews_count ?? data.count);

    if (Number.isFinite(rating) && rating > 0) {
      this.dataset.rating = String(rating);
    }
    if (Number.isFinite(count) && count > 0) {
      this.dataset.reviewCount = String(count);
    }

    this.#renderSummary();
  }

  #renderSummary() {
    if (!this.#rating) return;

    const rating = Number(this.dataset.rating);
    const count = Number(this.dataset.reviewCount);
    if (!Number.isFinite(rating) || !Number.isFinite(count) || rating <= 0) return;

    const formatted = rating.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
    const formatReviewNumber = (value) => {
      const arabic = window.ParajohnArabicNumerals;
      if (arabic?.isArabic && typeof arabic.toEastern === 'function') {
        return arabic.toEastern(value);
      }
      return String(value);
    };

    this.#rating.replaceChildren();
    this.#rating.append(`${formatReviewNumber(formatted)} `);
    this.#rating.append(this.#starIcon('testimonials__summary-star'));
    this.#rating.append(` (${formatReviewNumber(count)})`);
  }

  /**
   * @param {string} html
   * @returns {TestimonialReview[]}
   */
  #parseHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const itemSelectors = [
      '[data-review-id]',
      '.jdgm-rev',
      '.jdgm-carousel-item',
      '.jdgm-carousel-item-wrapper',
      '.jdgm-carousel__item-wrapper',
      '.jdgm-carousel__item',
      '.jdgm-rev-card',
      '.jdgm-review-item',
      '.jm-review-item',
      '.jdgm-cards-carousel__item',
    ];

    let items = [];
    for (const selector of itemSelectors) {
      items = [...doc.querySelectorAll(selector)];
      if (items.length) break;
    }

    return items
      .map((item, index) => {
        const title = this.#textContent(
          item,
          '.jdgm-rev__title, .jdgm-carousel-item__review-title, .jdgm-rev-title, .jdgm-review-title, .jm-review-title, [class*="review-title"]'
        );
        const body = this.#textContent(
          item,
          '.jdgm-rev__body, .jdgm-carousel-item__review-body, .jdgm-carousel-item__review-content, .jdgm-carousel-item__review, .jdgm-rev-body, .jdgm-review-content, .jm-review-content, [class*="review-body"], p'
        );
        const name = this.#textContent(
          item,
          '.jdgm-rev__author, .jdgm-carousel-item__reviewer-name, .jdgm-rev-author, .jdgm-reviewer-name, .jm-reviewer-name, [class*="reviewer-name"]'
        );
        const scoreEl = item.querySelector('[data-score], .jdgm-star, .jdgm-rev-stars');
        const rating = Number(scoreEl?.getAttribute('data-score') || scoreEl?.getAttribute('data-rating') || 5);
        const imageEl = item.querySelector(
          '.jdgm-carousel-item__reviewer-picture img, .jdgm-rev-pic img, img[class*="reviewer"], img'
        );
        const image = this.#safeImageUrl(imageEl?.getAttribute('src'));

        if (!title && !body) return null;

        return {
          id: item.getAttribute('data-review-id') || `html-${index}`,
          title: title || body.split(/\s+/).slice(0, 6).join(' '),
          body,
          rating: Number.isFinite(rating) ? rating : 5,
          name: name || this.#anonymousLabel,
          image,
        };
      })
      .filter(Boolean);
  }

  /**
   * @param {unknown} item
   * @param {number} index
   * @returns {TestimonialReview | null}
   */
  #fromObject(item, index) {
    if (!item || typeof item !== 'object') return null;

    const review = /** @type {Record<string, any>} */ (item);
    const reviewer = review.reviewer && typeof review.reviewer === 'object' ? review.reviewer : {};
    const title = this.#plainText(review.title || review.review_title || '');
    const body = this.#plainText(review.body || review.content || review.review_body || review.comment || '');
    const name = this.#plainText(reviewer.name || review.reviewer_name || review.name || '');
    const rating = Number(review.rating || review.score || 5);
    const image =
      this.#safeImageUrl(reviewer.avatar || reviewer.picture || review.reviewer_picture) ||
      this.#pictureFromReview(review);

    if (!title && !body) return null;
    if (review.hidden === true || review.published === false || review.curated === 'spam') return null;

    return {
      id: String(review.uuid ?? review.id ?? index),
      title: title || body.split(/\s+/).slice(0, 6).join(' '),
      body,
      rating: Number.isFinite(rating) ? rating : 5,
      name: name || this.#anonymousLabel,
      image,
    };
  }

  /**
   * @param {Record<string, any>} review
   * @returns {string | undefined}
   */
  #pictureFromReview(review) {
    const urlList = review.pictures_urls;
    if (Array.isArray(urlList) && urlList.length) {
      const firstUrl = typeof urlList[0] === 'string' ? urlList[0] : urlList[0]?.url;
      const fromList = this.#safeImageUrl(firstUrl);
      if (fromList) return fromList;
    }

    const pictures = review.pictures || review.hidden_pictures || [];
    if (!Array.isArray(pictures) || !pictures.length) return undefined;

    const first = pictures[0];
    if (typeof first === 'string') return this.#safeImageUrl(first);
    if (first && typeof first === 'object') {
      return this.#safeImageUrl(first.urls?.small || first.urls?.original || first.url || first.src);
    }
    return undefined;
  }

  /**
   * @param {TestimonialReview[]} reviews
   */
  #renderReviews(reviews) {
    if (!this.#track || !this.#reviewTemplate) return;

    const fragment = document.createDocumentFragment();

    for (const review of reviews) {
      const node = this.#reviewTemplate.content.firstElementChild?.cloneNode(true);
      if (!(node instanceof HTMLElement)) continue;

      const title = node.querySelector('[data-review-title]');
      const body = node.querySelector('[data-review-body]');
      const name = node.querySelector('[data-review-name]');
      const stars = node.querySelector('[data-review-stars]');

      if (title) title.textContent = review.title;
      if (body) body.textContent = review.body;
      if (name) name.textContent = review.name;
      if (stars instanceof HTMLElement) {
        this.#paintStars(stars, review.rating);
        stars.setAttribute('aria-label', `${Math.round(review.rating)} out of 5`);
      }

      fragment.append(node);
    }

    this.#track.replaceChildren(fragment);
  }

  /**
   * @param {TestimonialReview[]} reviews
   */
  #renderAvatars(reviews) {
    if (!this.#avatars) return;

    const images = [];
    for (const review of reviews) {
      if (review.image && !images.includes(review.image)) images.push(review.image);
      if (images.length === 4) break;
    }

    const names = reviews.map((review) => review.name).filter(Boolean);
    this.#avatars.replaceChildren();

    const count = Math.max(images.length, Math.min(4, names.length || 4));
    for (let index = 0; index < count; index += 1) {
      const wrapper = document.createElement('span');
      wrapper.className = 'testimonials__avatar';
      wrapper.style.zIndex = String(index + 1);

      if (images[index]) {
        const img = document.createElement('img');
        img.src = images[index];
        img.alt = '';
        img.width = 50;
        img.height = 57;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.referrerPolicy = 'no-referrer';
        wrapper.append(img);
      } else {
        wrapper.textContent = this.#initials(names[index] || this.#anonymousLabel);
      }

      this.#avatars.append(wrapper);
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {number} rating
   */
  #paintStars(root, rating) {
    root.replaceChildren();
    const filled = Math.round(Math.min(5, Math.max(0, rating)));

    for (let index = 1; index <= 5; index += 1) {
      const svg = this.#starIcon('testimonials__star');
      if (index > filled) svg.classList.add('testimonials__star--empty');
      root.append(svg);
    }
  }

  /**
   * @param {string} className
   * @returns {SVGSVGElement}
   */
  #starIcon(className) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', className);

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', STAR_PATH);
    svg.append(path);
    return svg;
  }

  /**
   * @param {Element} root
   * @param {string} selector
   * @returns {string}
   */
  #textContent(root, selector) {
    const el = root.querySelector(selector);
    return this.#plainText(el?.textContent || '');
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #plainText(value) {
    const template = document.createElement('textarea');
    template.innerHTML = value;
    return (template.value || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  /**
   * @param {string | null | undefined} url
   * @returns {string | undefined}
   */
  #safeImageUrl(url) {
    if (!url || typeof url !== 'string') return undefined;
    const trimmed = url.trim();
    if (!/^https:\/\//i.test(trimmed)) return undefined;
    if (/[<>"']/.test(trimmed)) return undefined;

    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== 'https:') return undefined;
      return parsed.href;
    } catch {
      return undefined;
    }
  }

  /**
   * @param {string} name
   * @returns {string}
   */
  #initials(name) {
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map((part) => part.charAt(0).toUpperCase()).join('') || 'A';
  }

  /**
   * @param {string} message
   */
  #setStatus(message) {
    if (!this.#status) return;
    this.#status.textContent = message;
    this.#status.hidden = !message;
  }

  /**
   * @returns {HTMLElement | null}
   */
  get #scrollRoot() {
    return this.#track;
  }

  /**
   * @param {number} direction
   */
  #scrollBy(direction) {
    const root = this.#scrollRoot;
    if (!root) return;

    const card =
      root.querySelector('.testimonials__review') || root.querySelector('.testimonials__intro');
    const styles = getComputedStyle(root);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 21;
    const width = card instanceof HTMLElement ? card.getBoundingClientRect().width : 267;
    const distance = direction * (width + gap);
    const delta = styles.direction === 'rtl' ? -distance : distance;

    if (typeof root.scrollBy === 'function') {
      root.scrollBy({ left: delta, behavior: 'smooth' });
    } else {
      root.scrollLeft += delta;
    }

    requestAnimationFrame(() => this.#syncNav());
  }

  #syncNav = () => {
    const root = this.#scrollRoot;
    if (!root) return;

    const maxScroll = Math.max(0, root.scrollWidth - root.clientWidth);
    const position = Math.abs(root.scrollLeft);
    const atStart = position <= 4;
    const atEnd = position >= maxScroll - 4;
    const canScroll = maxScroll > 4;

    if (this.#prev) this.#prev.disabled = !canScroll || atStart;
    if (this.#next) this.#next.disabled = !canScroll || atEnd;
    this.#syncProgress(root, maxScroll);
  };

  /**
   * @param {HTMLElement} root
   * @param {number} maxScroll
   */
  #syncProgress(root, maxScroll) {
    if (!this.#progress || !this.#progressFill) return;

    const count = this.#track?.querySelectorAll('.testimonials__review').length || 0;
    const show = count > 1 && !this.#desktopQuery?.matches;
    this.#progress.hidden = !show;
    if (!show) return;

    const ratio = maxScroll > 0 ? Math.abs(root.scrollLeft) / maxScroll : 0;
    const fill = ((ratio * (count - 1) + 1) / count) * 100;
    this.#progressFill.style.width = `${Math.min(100, Math.max(100 / count, fill))}%`;
  }
}

if (!customElements.get('testimonials-carousel')) {
  customElements.define('testimonials-carousel', TestimonialsCarousel);
}
